import useSystemAdmin from "@/hooks/useSystemAdmin";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Loader2, UserX, UserCheck } from "lucide-react";
import { toast } from "sonner";

/**
 * Displays a list of pending users and allows administrators to approve or deny them.
 * It fetches the list of pending users from the system and provides action buttons
 * for each user.
 */
const PendingUserList = () => {
  const {
    pendingUsers,
    isLoading,
    error,
    approveUser,
    deleteUser,
    refetchPendingUsers,
  } = useSystemAdmin();

  const handleApprove = async (userId: string) => {
    try {
      await approveUser(userId);
      toast.success("User approved successfully.");
    } catch (err) {
      toast.error("Failed to approve user.");
    }
  };

  const handleDeny = async (userId: string) => {
    try {
      await deleteUser(userId);
      toast.success("User denied successfully.");
    } catch (err) {
      toast.error("Failed to deny user.");
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center text-red-500 flex flex-col items-center gap-2">
        <span>{error.message}</span>
        <Button variant="outline" size="sm" onClick={() => refetchPendingUsers()}>
          Retry
        </Button>
      </div>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Pending User Approvals</CardTitle>
        <CardDescription>
          The following users are waiting for account approval.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Username</TableHead>
              <TableHead>Email</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {pendingUsers && pendingUsers.length > 0 ? (
              pendingUsers.map((user) => (
                <TableRow key={user.id}>
                  <TableCell>{user.username}</TableCell>
                  <TableCell>{user.email}</TableCell>
                  <TableCell className="text-right">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleApprove(user.id)}
                      className="text-green-600 hover:text-green-700"
                    >
                      <UserCheck className="h-4 w-4 mr-1" />
                      Approve
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDeny(user.id)}
                      className="text-red-600 hover:text-red-700"
                    >
                      <UserX className="h-4 w-4 mr-1" />
                      Deny
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={3} className="text-center">
                  No pending users.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
};

export default PendingUserList;
